import type { CSSProperties, ReactNode } from "react";
import { Trans } from "react-i18next";
import { cn } from "@/lib/utils.ts";

type Denomination = {
  value: number;
  src: string;
  sizeMm: number;
  // Licence credit the image needs while it's on screen (see public/cash/SOURCES.md).
  credit?: string;
};

const BELSON_303 = "Belson 303, CC BY-SA 4.0";
const MARK_MORGAN = "Mark Morgan, CC BY 2.0";

// sizeMm is the coin diameter / note width, so the pictures keep real proportions.
const COINS: Record<string, Denomination[]> = {
  HUF: [
    { value: 5, src: "/cash/huf-coin-5.png", sizeMm: 21.2 },
    { value: 10, src: "/cash/huf-coin-10.png", sizeMm: 24.8 },
    { value: 20, src: "/cash/huf-coin-20.png", sizeMm: 26.3 },
    { value: 50, src: "/cash/huf-coin-50.png", sizeMm: 27.4 },
    { value: 100, src: "/cash/huf-coin-100.png", sizeMm: 23.8 },
    { value: 200, src: "/cash/huf-coin-200.png", sizeMm: 28.3 },
  ],
  EUR: [
    {
      value: 0.01,
      src: "/cash/eur-coin-0.01.gif",
      sizeMm: 16.25,
      credit: "European Commission",
    },
    {
      value: 0.02,
      src: "/cash/eur-coin-0.02.gif",
      sizeMm: 18.75,
      credit: BELSON_303,
    },
    {
      value: 0.05,
      src: "/cash/eur-coin-0.05.gif",
      sizeMm: 21.25,
      credit: BELSON_303,
    },
    {
      value: 0.1,
      src: "/cash/eur-coin-0.1.gif",
      sizeMm: 19.75,
      credit: BELSON_303,
    },
    {
      value: 0.2,
      src: "/cash/eur-coin-0.2.gif",
      sizeMm: 22.25,
      credit: BELSON_303,
    },
    {
      value: 0.5,
      src: "/cash/eur-coin-0.5.gif",
      sizeMm: 24.25,
      credit: BELSON_303,
    },
    {
      value: 1,
      src: "/cash/eur-coin-1.gif",
      sizeMm: 23.25,
      credit: BELSON_303,
    },
    {
      value: 2,
      src: "/cash/eur-coin-2.jpg",
      sizeMm: 25.75,
      credit: MARK_MORGAN,
    },
  ],
};

const NOTES: Record<string, Denomination[]> = {
  HUF: [500, 1000, 2000, 5000, 10000, 20000].map((value) => ({
    value,
    src: `/cash/huf-note-${value}.jpg`,
    sizeMm: 154,
  })),
  EUR: [
    { value: 5, src: "/cash/eur-note-5.jpg", sizeMm: 120 },
    { value: 10, src: "/cash/eur-note-10.jpg", sizeMm: 127 },
    { value: 20, src: "/cash/eur-note-20.jpg", sizeMm: 133 },
    { value: 50, src: "/cash/eur-note-50.jpg", sizeMm: 140 },
    { value: 100, src: "/cash/eur-note-100.jpg", sizeMm: 147 },
    { value: 200, src: "/cash/eur-note-200.jpg", sizeMm: 153 },
  ],
};

export const COIN_VALUES: Record<string, number[]> = {
  HUF: COINS.HUF.map((coin) => coin.value),
  EUR: COINS.EUR.map((coin) => coin.value),
};

export const NOTE_VALUES: Record<string, number[]> = {
  HUF: NOTES.HUF.map((note) => note.value),
  EUR: NOTES.EUR.map((note) => note.value),
};

const COIN_PX_PER_MM = 4.2;
const NOTE_PX_PER_MM = 1.05;

export function AcceptedCashDisplay({
  currencyCode,
  acceptedCoinValues,
  acceptedNoteValues,
}: {
  currencyCode: string;
  acceptedCoinValues: number[];
  acceptedNoteValues: number[] | null;
}) {
  const currency = currencyCode === "EUR" ? "EUR" : "HUF";

  return (
    <div className="flex flex-col gap-y-8 bg-[#e8f7f9] px-10 py-9 short:gap-y-5 short:py-6">
      <CashRow label={<Trans>Elfogadott érmék</Trans>}>
        {COINS[currency].map((coin) => (
          <CashPicture
            key={coin.value}
            denomination={coin}
            width={coin.sizeMm * COIN_PX_PER_MM}
            shape="coin"
            accepted={acceptedCoinValues.includes(coin.value)}
          />
        ))}
      </CashRow>
      {acceptedNoteValues && (
        <CashRow label={<Trans>Elfogadott bankjegyek</Trans>}>
          {NOTES[currency].map((note) => (
            <CashPicture
              key={note.value}
              denomination={note}
              width={note.sizeMm * NOTE_PX_PER_MM}
              shape="note"
              accepted={acceptedNoteValues.includes(note.value)}
            />
          ))}
        </CashRow>
      )}
    </div>
  );
}

function CashRow({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="mb-4 font-helvetica text-[30px] font-bold leading-none text-bub-dark-blue">
        {label}
      </p>
      <div className="flex items-center justify-between gap-x-3">
        {children}
      </div>
    </div>
  );
}

// Layers are stacked with grid instead of absolute positioning so they never
// paint above the (unpositioned z-index) modal backdrop.
function CashPicture({
  denomination,
  shape,
  width,
  accepted,
}: {
  shape: "coin" | "note";
  denomination: Denomination;
  width: number;
  accepted: boolean;
}) {
  return (
    // The real-proportion width is only a maximum: items shrink to share the
    // row, so the tray never overflows a narrower kiosk screen.
    <div
      className="grid min-w-0 flex-1 *:[grid-area:1/1]"
      style={{ maxWidth: width }}
    >
      <img
        src={denomination.src}
        alt={String(denomination.value)}
        className={cn(
          "w-full",
          shape === "coin"
            ? "rounded-full mix-blend-multiply"
            : "rounded-md shadow-[0_3px_8px_rgba(0,59,85,0.2)]",
          !accepted && "opacity-50 grayscale",
        )}
      />
      {!accepted && (
        <div
          className={cn(shape === "coin" && "rounded-full")}
          style={strikeStyle}
        />
      )}
    </div>
  );
}

const strikeStyle: CSSProperties = {
  background:
    "linear-gradient(to top right, transparent calc(50% - 4px), #f45e22 calc(50% - 4px), #f45e22 calc(50% + 4px), transparent calc(50% + 4px))",
};

// The credit line for the pictures AcceptedCashDisplay shows with the same props.
export function CashImageCredits({
  currencyCode,
  showNotes,
}: {
  currencyCode: string;
  showNotes: boolean;
}) {
  const currency = currencyCode === "EUR" ? "EUR" : "HUF";
  const visible = [...COINS[currency], ...(showNotes ? NOTES[currency] : [])];
  const credits = [...new Set(visible.flatMap((item) => item.credit ?? []))];

  if (credits.length === 0) {
    return null;
  }

  return (
    <p className="text-[14px] leading-tight text-[#7c8a92]">
      {`Images: ${credits.join("; ")} – via Wikimedia Commons`}
    </p>
  );
}
